// Import the necessary components
import VerofreshRecord from './VerofreshRecord'
import VerofreshHttpAdapter from '@/orm/VeroFreshHttpAdapter'
import { get } from 'lodash'
import { $orm } from '@/orm'

class UserRecord extends VerofreshRecord {
  static getRoleNames () {
    return {
      root: 'Root',
      user: 'User',
      admin: 'Admin',
      rep: 'Rep',
      api: 'API User',
      driver: 'Driver'
    }
  }

  static getHomePageRoutes () {
    return {
      create_a_route: '/route/create',
      route_dashboard: '/routes',
      route_archive: '/route/index',
      root_cause_analysis: '/root-causes',
      root_cause_archive: '/root-cause/index',
      vehicles: '/vehicles',
      reports: '/report/index',
      user_settings: '/user/settings'
    }
  }

  /**
   *
   * @param {String} permissionCode
   * @param {String} scope can be all, own, or * (to mean any scope)
   * @return {Boolean}
   */
  can (permissionCode, scope = '*') {
    if (get(this, 'role_permissions', []).length === 0) {
      return false
    }

    return this.role_permissions.filter(rolePermission => {
      // first make sure the permission code matches
      if (rolePermission.permission_code !== permissionCode) {
        return false
      }

      // We have a matching permission code. If the scope is * (any) return true
      // because we don't care about what kind of scope it is, just that they
      // have this permission
      if (scope === '*') {
        return true
      }

      // If the scope is something other than * (any), check to make sure they
      // have the correct scope
      return rolePermission.scope === scope
    }).length > 0
  };

  /**
   * Returns true if the user has any of the permissions specified in the
   * permission codes array
   * @param Array permissionCodes array of codes
   * @param String scope one of * (wildcard), all, or own. Defaults to *
   * @return Boolean
   */
  canAny (permissionCodes, scope = '*') {
    let hasPermission = false

    permissionCodes.forEach(permissionCode => {
      if (this.can(permissionCode, scope)) {
        hasPermission = true
      }
    })

    return hasPermission
  };

  getSetting (settingKey, defaultValue = null) {
    if (this.user_settings === undefined) {
      return defaultValue
    }

    const setting = this.user_settings.filter(setting =>
      setting.key === settingKey
    )

    if (setting.length === 0) {
      return defaultValue
    }

    return setting[0].value
  };

  /**
   * Update the user's settings
   * @param settings A JSON object containing the settings to update/create
   * @return {AxiosPromise}
   */
  updateSettings (settings) {
    return VerofreshHttpAdapter({
      url: $orm('User').endpoint + '/' + this.id + '/update-settings',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: JSON.stringify(settings)
    })
  }

  /**
   * Assigns the user the given role
   *
   * @param int roleId
   * @return promise
   */
  assignRole (roleId) {
    return this._mapper()
      .datastore
      .getMapper('User')
      .assignRole({
        user_id: this.id,
        data: {
          role_id: roleId,
          user_id: this.id
        }
      })
  }

  /**
   *
   * @param int roleId
   * @return promise
   */
  removeRole (roleId) {
    return this._mapper()
      .datastore
      .getMapper('User')
      .removeRole({
        role_id: roleId,
        user_id: this.id
      })
  }

  /**
   * Assigns the user the given customer
   *
   * @param int customerId
   * @return promise
   */
  assignCustomer (customerId) {
    return this._mapper()
      .datastore
      .getMapper('User')
      .assignCustomer({
        user_id: this.id,
        data: {
          customer_id: customerId,
          user_id: this.id
        }
      })
  }

  /**
   * Removes the user from the given customer
   *
   * @param int customerId
   * @return promise
   */
  removeCustomer (customerId) {
    return this._mapper()
      .datastore
      .getMapper('User')
      .removeCustomer({
        customer_id: customerId,
        user_id: this.id
      })
  }
}

// Actions allow us to defined additional calls we can make to related
// endpoints.
const actions = {
  assignRole: {
    method: 'POST',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.user_id}/role`
    },
    response (response) {
      return response.data
    }
  },
  removeRole: {
    method: 'DELETE',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.user_id}/role/${options.role_id}`
    },
    response (response) {
      return response.data
    }
  },
  assignCustomer: {
    method: 'POST',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.user_id}/customer`
    },
    response (response) {
      return response.data
    }
  },
  removeCustomer: {
    method: 'DELETE',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.user_id}/customer/${options.customer_id}`
    },
    response (response) {
      return response.data
    }
  }
}

const relations = {
  hasOne: {
    ResourceRestrictionRuleSet: {
      foreignKey: 'entity_id',
      localField: 'resource_restriction_rule_set'
    }
  },
  hasMany: {
    Role: {
      foreignKey: 'id',
      localField: 'roles'
    },
    Customer: {
      foreignKey: 'id',
      localField: 'customers'
    }
  }
}

export default UserRecord
export { actions, relations }
